// Fleet-wide contracts pipeline: a kanban-style 3-column view over every
// contract in the control plane (draft → awaiting signature → signed), each
// card linking to its tenant. Contracts that ended without a signature
// (declined / void / expired) are grouped below the board.

import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import {
  createContractTemplate,
  deleteContractTemplate,
  listAllContracts,
  listContractTemplates,
  updateContractTemplate,
} from "../lib/api";
import { useApi, useMutation } from "../lib/useApi";
import { formatDateShort, formatMoney } from "../lib/format";
import type { ContractTemplateRow, FleetContractRow } from "../lib/types";
import { TopBar } from "../components/Layout";
import { ContractBadge } from "../components/Badge";
import { Alert, EmptyState, ErrorState, Loading, Spinner } from "../components/States";
import { Modal } from "../components/Modal";

export function ContractsPage() {
  const { data, loading, error, reload } = useApi((signal) => listAllContracts(signal), []);
  const contracts = data?.contracts ?? [];

  const draft = contracts.filter((c) => c.status === "draft");
  // "viewed" is still awaiting a signature — it sits in the same column as "sent".
  const awaiting = contracts.filter((c) => c.status === "sent" || c.status === "viewed");
  const signed = contracts.filter((c) => c.status === "signed");
  const closed = contracts.filter(
    (c) => c.status === "declined" || c.status === "void" || c.status === "expired",
  );

  return (
    <>
      <TopBar
        crumbs="Contracts"
        actions={
          <button className="btn" onClick={reload} disabled={loading}>
            Refresh
          </button>
        }
      />
      <div className="content">
        <div className="page-head">
          <div>
            <h1>Contracts</h1>
            <div className="sub">
              Every e-sign contract across the fleet. Issue new ones from a tenant's detail page.
            </div>
          </div>
        </div>

        {loading ? (
          <Loading label="Loading contracts…" />
        ) : error ? (
          <ErrorState error={error} onRetry={reload} />
        ) : contracts.length === 0 ? (
          <div className="card">
            <div className="card-body">
              <div className="state muted">
                No contracts issued yet. Open a tenant and use “Issue contract” to start the
                pipeline.
              </div>
            </div>
          </div>
        ) : (
          <>
            <div className="pipeline">
              <PipelineColumn title="Draft" items={draft} />
              <PipelineColumn title="Awaiting signature" items={awaiting} showWaiting />
              <PipelineColumn title="Signed" items={signed} />
            </div>
          </>
        )}

        <TemplatesManager />

        {!loading && !error ? (
          <>
            {closed.length > 0 ? (
              <div className="card" style={{ marginTop: 18 }}>
                <div className="card-head">
                  <h2>Closed without signature</h2>
                  <span className="pill">{closed.length}</span>
                </div>
                <div className="card-body tight">
                  <table className="data">
                    <thead>
                      <tr>
                        <th>Title</th>
                        <th>Tenant</th>
                        <th>Status</th>
                        <th>Amount</th>
                        <th>Created</th>
                      </tr>
                    </thead>
                    <tbody>
                      {closed.map((c) => (
                        <tr key={c.id}>
                          <td>
                            <strong>{c.title}</strong>
                          </td>
                          <td>
                            <Link to={`/tenants/${c.tenant_id}`}>{c.tenant_name}</Link>
                          </td>
                          <td>
                            <ContractBadge status={c.status} />
                          </td>
                          <td className="num">{formatMoney(c.amount_cents, c.currency)}</td>
                          <td className="num">{formatDateShort(c.created_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : null}
          </>
        ) : null}
      </div>
    </>
  );
}

const DEFAULT_TEMPLATE_BODY = `# Service Agreement

This agreement is entered into between **ActionConnect** and **{{tenant_name}}** on {{date}}.

## Term
- Workspace: {{tenant_slug}}.actionconnect.com
- Effective date: {{date}}

## Acceptance
By signing below, the undersigned agrees to the terms of service.`;

/** Manage reusable contract templates (author markdown w/ {{placeholders}}).
 *  Lives under the fleet contracts pipeline; templates feed the per-tenant
 *  "Issue contract" modal. */
function TemplatesManager() {
  const { data, loading, error, reload } = useApi((signal) => listContractTemplates(signal), []);
  const templates = data?.templates ?? [];
  const [editing, setEditing] = useState<ContractTemplateRow | "new" | null>(null);
  const del = useMutation(deleteContractTemplate);

  async function onDelete(t: ContractTemplateRow) {
    if (!window.confirm(`Delete template "${t.name}"? Issued contracts keep their signed copy.`)) {
      return;
    }
    if (await del.run(t.id)) reload();
  }

  return (
    <div className="card" style={{ marginTop: 18 }}>
      <div className="card-head">
        <h2>Contract templates</h2>
        <button className="btn primary small" onClick={() => setEditing("new")}>
          + New template
        </button>
      </div>
      <div className="card-body tight">
        {del.error ? <Alert kind="error">{del.error}</Alert> : null}
        {loading ? (
          <Loading label="Loading templates…" />
        ) : error ? (
          <ErrorState error={error} onRetry={reload} />
        ) : templates.length === 0 ? (
          <EmptyState>
            No templates yet. Create one with {"{{placeholders}}"} like {"{{tenant_name}}"} to reuse
            when issuing contracts.
          </EmptyState>
        ) : (
          <table className="data">
            <thead>
              <tr>
                <th>Name</th>
                <th>Code</th>
                <th>Updated</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {templates.map((t) => (
                <tr key={t.id}>
                  <td>
                    <strong>{t.name}</strong>
                  </td>
                  <td className="mono">{t.code}</td>
                  <td className="num" style={{ whiteSpace: "nowrap" }}>
                    {formatDateShort(t.updated_at)}
                  </td>
                  <td>
                    <div className="btn-row">
                      <button className="btn ghost small" onClick={() => setEditing(t)}>
                        Edit
                      </button>
                      <button
                        className="btn ghost small danger"
                        onClick={() => onDelete(t)}
                        disabled={del.pending}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {editing ? (
        <TemplateEditorModal
          template={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            reload();
          }}
        />
      ) : null}
    </div>
  );
}

function TemplateEditorModal({
  template,
  onClose,
  onSaved,
}: {
  template: ContractTemplateRow | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const isNew = template === null;
  const [code, setCode] = useState(template?.code ?? "");
  const [name, setName] = useState(template?.name ?? "");
  const [bodyMd, setBodyMd] = useState(template?.body_md ?? DEFAULT_TEMPLATE_BODY);
  const create = useMutation(createContractTemplate);
  const update = useMutation(updateContractTemplate);
  const pending = create.pending || update.pending;
  const err = create.error ?? update.error;
  const canSave = (isNew ? code.trim() : true) && name.trim() && bodyMd.trim();

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!canSave) return;
    const res = isNew
      ? await create.run({ code: code.trim(), name: name.trim(), bodyMd })
      : await update.run(template.id, { name: name.trim(), bodyMd });
    if (res) onSaved();
  }

  return (
    <Modal
      title={isNew ? "New contract template" : `Edit ${template.name}`}
      wide
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose} disabled={pending}>
            Cancel
          </button>
          <button
            className="btn primary"
            type="submit"
            form="template-form"
            disabled={pending || !canSave}
          >
            {pending ? <Spinner label="Saving…" /> : "Save template"}
          </button>
        </>
      }
    >
      {err ? <Alert kind="error">{err}</Alert> : null}
      <form id="template-form" onSubmit={onSubmit}>
        <div className="row">
          <label className="field">
            <span className="label">Name</span>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Master Service Agreement"
              autoFocus
              required
            />
          </label>
          <label className="field">
            <span className="label">Code</span>
            <input
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="msa"
              disabled={!isNew}
              required={isNew}
            />
          </label>
        </div>
        <label className="field">
          <span className="label">Body (Markdown)</span>
          <textarea value={bodyMd} onChange={(e) => setBodyMd(e.target.value)} rows={16} required />
          <span className="hint">
            Markdown with {"{{placeholders}}"}. Available: {"{{tenant_name}}"}, {"{{tenant_slug}}"},{" "}
            {"{{date}}"}. Rendered + hashed into an immutable snapshot when a contract is issued.
          </span>
        </label>
      </form>
    </Modal>
  );
}

/** Whole days a sent contract has been waiting on a signature. */
function waitingDays(c: FleetContractRow): number {
  const since = c.sent_at ?? c.created_at;
  return Math.floor((Date.now() / 1000 - since) / 86400);
}

function PipelineColumn({
  title,
  items,
  showWaiting = false,
}: {
  title: string;
  items: FleetContractRow[];
  showWaiting?: boolean;
}) {
  return (
    <section className="pipeline-col" aria-label={`${title} (${items.length})`}>
      <h3>
        {title}
        <span className="pill">{items.length}</span>
      </h3>
      {items.length === 0 ? (
        <div className="pipeline-empty">Nothing here.</div>
      ) : (
        items.map((c) => <ContractCard key={c.id} contract={c} showWaiting={showWaiting} />)
      )}
    </section>
  );
}

function ContractCard({
  contract: c,
  showWaiting,
}: {
  contract: FleetContractRow;
  showWaiting: boolean;
}) {
  const days = waitingDays(c);
  return (
    <article className="pipeline-card">
      <div className="pipeline-card-top">
        <strong>{c.title}</strong>
        <ContractBadge status={c.status} />
      </div>
      <div className="pipeline-card-meta">
        <Link to={`/tenants/${c.tenant_id}`}>{c.tenant_name}</Link>
        <span className="mono faint">{c.tenant_slug}</span>
      </div>
      <div className="pipeline-card-meta">
        <span>{formatMoney(c.amount_cents, c.currency)}</span>
        {c.term_months != null ? <span className="faint">{c.term_months} mo</span> : null}
        <span className="faint">
          {c.signed_count}/{c.signer_count} signed
        </span>
      </div>
      <div className="pipeline-card-foot">
        {showWaiting ? (
          <span className={`pill${days >= 7 ? " urgent" : ""}`}>
            awaiting signature for {days} day{days === 1 ? "" : "s"}
          </span>
        ) : c.status === "signed" && c.signed_at ? (
          <span className="faint">signed {formatDateShort(c.signed_at)}</span>
        ) : (
          <span className="faint">created {formatDateShort(c.created_at)}</span>
        )}
      </div>
    </article>
  );
}
